import { useEffect, useState } from "react";
import { useParams, Link, useOutletContext } from "react-router-dom";
import api from "@/lib/api";
import { getInitials } from "@/lib/initials";
import { PaperCard } from "@/components/PaperCard";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Paragraphs } from "@/lib/usePageContent";
import { Loader2, ChevronRight, Award, Calendar, Building2, UploadCloud, BookOpen, Users, Crown, Mail, Briefcase } from "lucide-react";

const ROLE_ORDER = ["Associate Editor", "Section Editor", "Managing Editor", "Editorial Board Member", "Guest Editor", "Expert Reviewer", "Advisory Board"];

function MemberCard({ name, role, designation, department, affiliation, email, photo, lead, to }) {
  const initials = getInitials(name);
  const BACKEND = process.env.REACT_APP_BACKEND_URL;
  const deptLine = [designation, department].filter(Boolean).join(", ");
  const inner = (
    <div className={`flex items-start gap-3 rounded-xl border p-4 transition-colors ${lead ? "border-amber-300 dark:border-amber-700 bg-amber-50/40 dark:bg-amber-950/10" : "border-slate-200 dark:border-slate-800 bg-card"} ${to ? "hover:border-primary/50 hover:shadow-sm cursor-pointer" : ""}`}>
      {photo
        ? <img src={`${BACKEND}${photo}`} alt={name} className="w-11 h-11 rounded-full object-cover shrink-0" />
        : <span className={`grid place-items-center w-11 h-11 rounded-full shrink-0 font-semibold text-sm ${lead ? "bg-amber-500 text-white" : "bg-primary/10 text-primary dark:bg-primary/20 dark:text-sky-300"}`}>{initials}</span>}
      <div className="min-w-0">
        <p className="font-semibold text-slate-900 dark:text-slate-100 flex items-center gap-1.5">{name}{lead && <Crown className="w-3.5 h-3.5 text-amber-500" />}</p>
        {role && <p className="text-xs font-medium text-primary dark:text-sky-400">{role}</p>}
        {deptLine && <p className="text-xs text-slate-600 dark:text-slate-300 mt-0.5 flex items-center gap-1"><Briefcase className="w-3 h-3 shrink-0" /> {deptLine}</p>}
        {affiliation && <p className="text-xs text-slate-500 mt-0.5">{affiliation}</p>}
        {email && <p className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 break-all"><Mail className="w-3 h-3 shrink-0" /> {email}</p>}
      </div>
    </div>
  );
  return to ? <Link to={to} className="block">{inner}</Link> : inner;
}

export default function JournalDetail() {
  const { slug } = useParams();
  const { settings } = useOutletContext();
  const hideIF = settings?.show_impact_factor === false;
  const [journal, setJournal] = useState(null);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/journals/${slug}`).then(({ data }) => setJournal(data)).finally(() => setLoading(false));
    api.get(`/journals/${slug}/issues`).then(({ data }) => setIssues(data)).catch(() => {});
  }, [slug]);

  if (loading) return <div className="min-h-[50vh] grid place-items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!journal) return <div className="max-w-7xl mx-auto px-6 py-20">Journal not found.</div>;

  const totalPapers = issues.reduce((n, i) => n + i.papers.length, 0);
  const board = journal.editorial_board || [];
  const groups = {};
  board.forEach((m, idx) => { const r = m.role || "Editorial Board Member"; (groups[r] = groups[r] || []).push({ ...m, _idx: idx }); });
  const groupedBoard = Object.keys(groups).sort((a, b) => {
    const ia = ROLE_ORDER.indexOf(a), ib = ROLE_ORDER.indexOf(b);
    return (ia < 0 ? 99 : ia) - (ib < 0 ? 99 : ib);
  }).map((r) => [r, groups[r]]);
  const boardCount = board.length + (journal.editor_in_chief ? 1 : 0);

  return (
    <div>
      <div className="bg-primary text-white">
        <div className="max-w-7xl mx-auto px-6 py-14 grid md:grid-cols-[200px_1fr] gap-8 items-start">
          {journal.cover_image && (
            <img src={journal.cover_image} alt={journal.title} className="w-full rounded-lg shadow-xl hidden md:block bg-white/10" />
          )}
          <div>
            <div className="flex items-center gap-2 text-sm text-slate-400 mb-3">
              <Link to="/" className="hover:text-amber-400">Home</Link>
              <ChevronRight className="w-4 h-4" />
              {journal.category && <Link to={`/category/${journal.category.slug}`} className="hover:text-amber-400">{journal.category.name}</Link>}
            </div>
            <h1 className="font-serif font-black text-3xl md:text-4xl leading-tight">{journal.title}</h1>
            <p className="font-mono text-sm text-slate-400 mt-2">ISSN {journal.issn} · {journal.abbreviation}</p>
            <div className="flex flex-wrap gap-2 mt-4">
              {journal.impact_factor && !hideIF && <Badge className="bg-amber-500/20 text-amber-300 border-0 gap-1"><Award className="w-3.5 h-3.5" /> Impact Factor {journal.impact_factor}</Badge>}
              <Badge className="bg-white/10 text-slate-200 border-0 gap-1"><Calendar className="w-3.5 h-3.5" /> {journal.frequency}</Badge>
              <Badge className="bg-emerald-500/20 text-emerald-300 border-0">Open Access</Badge>
            </div>
            <div className="flex gap-3 mt-6">
              <Button asChild className="rounded-full bg-amber-500 hover:bg-amber-600 text-white" data-testid="journal-submit-btn">
                <Link to="/submit"><UploadCloud className="w-4 h-4 mr-1.5" /> Submit to this journal</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      <section className="max-w-7xl mx-auto px-6 py-12 grid lg:grid-cols-[1fr_320px] gap-10">
        <div>
          <Tabs defaultValue="articles">
            <TabsList data-testid="journal-tabs">
              <TabsTrigger value="articles" data-testid="tab-articles">Articles ({totalPapers})</TabsTrigger>
              <TabsTrigger value="board" data-testid="tab-board">Editorial Board ({boardCount})</TabsTrigger>
              <TabsTrigger value="about" data-testid="tab-aim-scope">Aim &amp; Scope</TabsTrigger>
            </TabsList>

            <TabsContent value="articles" className="mt-6 space-y-10">
              {totalPapers === 0 && <p className="text-slate-500">No articles published yet.</p>}
              {issues.filter((i) => i.papers.length > 0).map((iss) => (
                <div key={`${iss.volume}-${iss.issue}`} data-testid={`journal-issue-${iss.volume}-${iss.issue}`}>
                  <div className="flex gap-4 mb-5">
                    {iss.cover_image && (
                      <div className="w-24 h-32 rounded-lg overflow-hidden shrink-0 shadow-sm hidden sm:block"
                        style={{ backgroundImage: `url(${iss.cover_image})`, backgroundSize: "cover", backgroundPosition: "center" }} />
                    )}
                    <div>
                      <h3 className="font-serif font-bold text-xl flex items-center gap-2 text-slate-900 dark:text-slate-100">
                        <BookOpen className="w-5 h-5 text-amber-600" /> Volume {iss.volume}, Issue {iss.issue}
                      </h3>
                      {iss.title && <p className="text-sm font-medium text-primary dark:text-sky-400 mt-1">{iss.title}</p>}
                      {iss.editorial_note && <p className="text-sm text-slate-600 dark:text-slate-400 mt-2 italic max-w-2xl">{iss.editorial_note}</p>}
                    </div>
                  </div>
                  <div className="grid gap-4">{iss.papers.map((p) => <PaperCard key={p.id} paper={p} />)}</div>
                </div>
              ))}
            </TabsContent>

            <TabsContent value="board" className="mt-6" data-testid="journal-board-tab">
              {boardCount === 0 ? (
                <p className="text-slate-500">The editorial board for this journal will be announced soon.</p>
              ) : (
                <div className="space-y-8">
                  {journal.editor_in_chief && (
                    <div>
                      <h3 className="font-sans font-semibold text-sm uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> Editor-in-Chief</h3>
                      <div className="grid sm:grid-cols-2 gap-3"><MemberCard name={journal.editor_in_chief} role="Editor-in-Chief" designation={journal.eic_designation} department={journal.eic_department} affiliation={journal.eic_affiliation} email={journal.eic_email} photo={journal.eic_photo} lead to={`/journal/${slug}/member/eic`} /></div>
                    </div>
                  )}
                  {groupedBoard.map(([role, members]) => (
                    <div key={role} data-testid={`board-group-${role.replace(/\s+/g, "-").toLowerCase()}`}>
                      <h3 className="font-sans font-semibold text-sm uppercase tracking-wider text-slate-500 mb-3 flex items-center gap-2"><Users className="w-4 h-4 text-primary" /> {role}{members.length > 1 ? "s" : ""}</h3>
                      <div className="grid sm:grid-cols-2 gap-3">
                        {members.map((m) => <MemberCard key={m._idx} name={m.name} role={m.role} designation={m.designation} department={m.department} affiliation={m.affiliation} email={m.email} photo={m.photo} to={`/journal/${slug}/member/${m._idx}`} />)}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </TabsContent>

            <TabsContent value="about" className="mt-6" data-testid="journal-aim-scope">
              <h3 className="font-serif font-bold text-2xl mb-4 text-slate-900 dark:text-slate-100">Aim &amp; Scope</h3>
              <Paragraphs text={journal.scope || journal.description} className="text-slate-700 dark:text-slate-300 leading-relaxed" />
            </TabsContent>
          </Tabs>
        </div>

        <aside className="space-y-6">
          <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5">
            <h4 className="font-sans font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Journal Information</h4>
            <dl className="space-y-3 text-sm">
              <div className="flex items-start gap-2"><Building2 className="w-4 h-4 text-slate-400 mt-0.5" /><div><dt className="text-slate-500">Editor-in-Chief</dt><dd className="font-medium text-slate-900 dark:text-slate-200">{journal.editor_in_chief}</dd></div></div>
              <div className="flex items-start gap-2"><Calendar className="w-4 h-4 text-slate-400 mt-0.5" /><div><dt className="text-slate-500">Frequency</dt><dd className="font-medium text-slate-900 dark:text-slate-200">{journal.frequency}</dd></div></div>
              <div className="flex items-start gap-2"><Award className="w-4 h-4 text-slate-400 mt-0.5" /><div><dt className="text-slate-500">Article Processing Charge</dt><dd className="font-medium text-slate-900 dark:text-slate-200">{journal.apc}</dd></div></div>
            </dl>
          </div>
          <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="journal-stats">
            <h4 className="font-sans font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Journal Statistics</h4>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-4">
              <div data-testid="stat-pub-years"><dt className="text-xs text-slate-500">Publication years</dt><dd className="font-bold text-lg text-slate-900 dark:text-slate-100">{journal.stats?.year_start ? (journal.stats.year_start === journal.stats.year_end ? journal.stats.year_start : `${journal.stats.year_start}–${journal.stats.year_end}`) : "—"}</dd></div>
              <div data-testid="stat-pub-count"><dt className="text-xs text-slate-500">Publications</dt><dd className="font-bold text-lg text-slate-900 dark:text-slate-100">{(journal.stats?.publication_count ?? 0).toLocaleString()}</dd></div>
              <div data-testid="stat-views"><dt className="text-xs text-slate-500">Views</dt><dd className="font-bold text-lg text-slate-900 dark:text-slate-100">{(journal.stats?.views ?? 0).toLocaleString()}</dd></div>
              <div data-testid="stat-downloads"><dt className="text-xs text-slate-500">Downloads</dt><dd className="font-bold text-lg text-slate-900 dark:text-slate-100">{(journal.stats?.downloads ?? 0).toLocaleString()}</dd></div>
              <div data-testid="stat-citations"><dt className="text-xs text-slate-500">Citations</dt><dd className="font-bold text-lg text-slate-900 dark:text-slate-100">{(journal.stats?.citations ?? 0).toLocaleString()}</dd></div>
            </dl>
          </div>
          {journal.editorial_board?.length > 0 && (
            <div className="bg-card rounded-xl border border-slate-200 dark:border-slate-800 p-5" data-testid="journal-editorial-board">
              <h4 className="font-sans font-semibold text-sm uppercase tracking-wider text-slate-500 mb-4">Editorial Board</h4>
              <ul className="space-y-3">
                {journal.editorial_board.map((m, i) => (
                  <li key={i} data-testid={`board-member-${i}`}>
                    <p className="font-medium text-sm text-slate-900 dark:text-slate-200">{m.name}</p>
                    {(m.role || m.affiliation) && <p className="text-xs text-slate-500">{[m.role, m.affiliation].filter(Boolean).join(" · ")}</p>}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </section>
    </div>
  );
}
